import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import LegalLayout from './LegalLayout';
import Markdown from '../components/Markdown';
import { legalAPI } from '../api';

/**
 * LegalPage — single public component for /impressum, /privacy, /terms.
 *
 * The slug is read either from the URL param `:slug` (when mounted via the
 * generic route) or from a `slug` prop (when mounted via the legacy named
 * routes). Content is fetched from /api/legal/{slug} and rendered with the
 * shared LegalLayout chrome + the in-repo Markdown component.
 */
const VALID_SLUGS = ['impressum', 'privacy', 'terms'];

const LegalPage = ({ slug: propSlug }) => {
  const params = useParams();
  const slug = (propSlug || params.slug || '').toLowerCase();
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!VALID_SLUGS.includes(slug)) { setLoaded(true); return; }
    let cancelled = false;
    legalAPI.get(slug)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { /* show fallback below */ })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [slug]);

  if (!VALID_SLUGS.includes(slug)) {
    return <Navigate to="/" replace />;
  }

  // Loaded but no data — show a calm minimal frame so the page never breaks.
  if (loaded && !data) {
    return (
      <LegalLayout title={slug[0].toUpperCase() + slug.slice(1)} testId={`legal-${slug}-page`}>
        <p style={{ fontStyle: 'italic', color: 'var(--kahla-text-light)' }}>
          This page is being prepared. Please check back shortly.
        </p>
      </LegalLayout>
    );
  }

  // Pre-load placeholder — keeps the chrome stable, no spinner flash.
  if (!data) return <div style={{ minHeight: '100vh', background: 'var(--kahla-cream-light, #faf8f5)' }} />;

  return (
    <LegalLayout
      title={data.title}
      eyebrow={data.eyebrow}
      intro={data.intro}
      lastUpdated={data.updated_at}
      testId={`legal-${slug}-page`}
    >
      <Markdown source={data.body} />
    </LegalLayout>
  );
};

export default LegalPage;
